import { createApraoTheme } from "@aprao/common/src/createApraoTheme.js";
import { RtSharedReportData } from "@aprao/model/src/runtypes/RtReportData.js";
import type { ShapeSharedReportData } from "@aprao/model/src/shapes/ShapeReportData.js";
import { css } from "@emotion/react";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import * as Sentry from "@sentry/browser";
import ky from "ky";
import { useEffect, useState } from "react";

import {
  buildErrorConf,
  buildErrorInfo,
  defaultsConf,
  type ErrorConf,
} from "../app/common/appErrors.js";
import { MainEmptyState } from "../app/common/layout/MainEmptyState.js";
import { I18nProvider, provideMessages, useI18n } from "../common/ApraoI18n.js";
import { getWebEnvDetails } from "../common/getWebEnvDetails.js";
import { AppraisalReport } from "../report/AppraisalReport.js";

const ReportSharedPage = () => {
  const [reportData, setReportData] = useState<ShapeSharedReportData | null>(
    null,
  );
  const [errorConf, setErrorConf] = useState<ErrorConf | null>(null);

  useEffect(() => {
    ky.get(`${location.pathname}/data${location.search}`)
      .json()
      .then((data) => {
        setReportData(RtSharedReportData.parse(data));
      })
      .catch(async (cause: unknown) => {
        const conf = buildErrorConf(
          await buildErrorInfo("appraisalSharingLoad", cause),
        );

        if (!conf.knownError) {
          Sentry.captureException(cause);
        }

        setErrorConf(conf);
      })
      .catch(Sentry.captureException);
  }, []);

  // we need to check if the org that owns the report has full access

  const { zIndex, palette, spacing } = useTheme();
  const { msg } = useI18n();

  return (
    <>
      <CssBaseline />
      <AppBar
        css={css`
          z-index: ${zIndex.drawer + 1};
          background-color: ${palette.background.paper};

          @media print {
            display: none;
          }
        `}
        position="fixed"
        color="default"
        variant="outlined"
        elevation={0}
      >
        <Toolbar
          css={css`
            display: flex;
            gap: ${spacing(3)};
          `}
        >
          <a href="/app">
            <img
              css={css`
                display: block;
                height: ${spacing(3)};
              `}
              src={new URL("/assets/logo.svg", import.meta.url).href}
              alt={msg("global.logoAlt")}
            />
          </a>
          {reportData?.sharingLinkDetails && (
            <>
              <span
                css={css`
                  margin-right: auto;
                `}
              >
                {msg("report.sharedAppBarMessage")}
              </span>
              <Button
                href={`/app/appraisals/copy?sharinglinkdetails=${encodeURIComponent(
                  JSON.stringify(reportData.sharingLinkDetails),
                )}`}
                variant="contained"
                size="small"
                data-testid="ars__button--add-to-board"
              >
                Copy to your personal board
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>
      <div
        css={css`
          @media screen {
            // 64px of bar height
            padding-top: 64px;
          }
        `}
      >
        {errorConf && (
          <MainEmptyState
            heading={msg(`errorHeadings.${errorConf.phase}`)}
            text={
              errorConf.description == null ?
                <defaultsConf.description />
              : <errorConf.description />
            }
            data-testid={`ars__error-state-${errorConf.phase}-${errorConf.name}`}
          />
        )}
        {reportData && (
          <AppraisalReport
            project={reportData.project}
            appraisalDocument={reportData.appraisalDocument}
            mapImageUrl={reportData.mapImageUrl}
            logoUrl={reportData.logoUrl}
            updatedDate={reportData.updatedDate}
            isOperatedAssetSensitivityAnalysesEnabled={
              reportData.isOperatedAssetSensitivityAnalysesEnabled
            }
          />
        )}
      </div>
    </>
  );
};

const apraoTheme = createApraoTheme({
  disableAnimations: getWebEnvDetails().disableAnimations,
});

export const AppraisalReportShared = () => (
  <I18nProvider
    languages={navigator.languages}
    provideMessages={provideMessages}
  >
    <ThemeProvider theme={apraoTheme}>
      <ReportSharedPage />
    </ThemeProvider>
  </I18nProvider>
);
